/**
 * Your own matters — anything the chief executive wants to weigh that the
 * platform's records do not put on the desk.
 *
 * Four plain steps: say what the matter is, name the choices (with any
 * numbers behind them), say what matters and how much, and score what only
 * judgement can score. The room adds it up (`domain/ceoRoom.ts`), says what
 * would change the answer, keeps the discussion beside it, and records the
 * decision in the same log as the desk's.
 */
import { useMemo, useState } from "react";
import { askModel, hasModel, loadConfig } from "../../assistant/external";
import {
  answerMatter,
  BOARD_ROWS,
  evaluateMatter,
  flipSentence,
  materialForMatter,
  MATTER_TEMPLATES,
  matterFromTemplate,
  newId,
  SOURCE_LABEL,
  type BoardRowKey,
  type Criterion,
  type CriterionSource,
  type Matter,
  type MatterEvaluation,
  type MatterOption,
} from "../../domain/ceoRoom";
import { verifyFigures } from "../../domain/decision";
import { ROOM_MODEL_PROMPT } from "../../domain/decisionRoom";
import { money } from "../../domain/intelligence";
import { Button, Card, Field, Input, Select } from "../../design/components";
import { t, tn } from "../../i18n";
import c from "./CeoRoom.module.css";

export interface MattersProps {
  matters: Matter[];
  currency: string;
  who: string;
  openId: string | null;
  onOpen: (id: string | null) => void;
  /** save the whole list; resolves to where it landed */
  onSave: (next: Matter[]) => Promise<"platform" | "browser">;
  onDecide: (m: Matter, option: string, rationale: string, summary: string[]) => Promise<void>;
}

export function MattersTab({ matters, currency, who, openId, onOpen, onSave, onDecide }: MattersProps) {
  const [starting, setStarting] = useState(matters.length === 0);
  const open = matters.find((m) => m.id === openId) ?? null;

  const create = async (tplKey: string, title: string) => {
    const tpl = MATTER_TEMPLATES.find((x) => x.key === tplKey) ?? MATTER_TEMPLATES[MATTER_TEMPLATES.length - 1];
    const m = matterFromTemplate({ ...tpl, label: t(tpl.label), options: tpl.options.map((o) => t(o)), criteria: tpl.criteria.map((k) => ({ ...k, name: t(k.name) })) }, title, who);
    await onSave([m, ...matters]);
    onOpen(m.id);
    setStarting(false);
  };

  return (
    <div className={c.matters}>
      <aside className={c.matterList} aria-label={t("Your matters")}>
        <Button variant="primary" onClick={() => setStarting(true)} data-testid="new-matter">
          + {t("Bring a new matter")}
        </Button>
        {matters.length === 0 && <p className={c.muted}>{t("Nothing here yet. Bring anything you want to think through — a project, a hire, a supplier, a policy.")}</p>}
        {matters.map((m) => {
          const e = evaluateMatter(m);
          return (
            <button
              key={m.id}
              type="button"
              className={`${c.matterItem} ${m.id === open?.id ? c.matterItemOn : ""}`}
              onClick={() => {
                onOpen(m.id);
                setStarting(false);
              }}
              aria-pressed={m.id === open?.id}
              data-testid={`matter-${m.id}`}
            >
              <span className={c.matterItemTitle}>{m.title}</span>
              <span className={c.matterItemSub}>
                {m.decided
                  ? t("Decided: {option}", { option: m.options.find((o) => o.key === m.decided!.option)?.name ?? m.decided.option })
                  : e.leader
                    ? t("{option} leads, {score}/100", { option: e.leader.name, score: e.leader.total })
                    : t("not weighed yet")}
              </span>
            </button>
          );
        })}
      </aside>

      <div className={c.matterMain}>
        {starting && <StartMatter onCreate={(k, title) => void create(k, title)} onCancel={matters.length ? () => setStarting(false) : undefined} />}
        {!starting && open && (
          <MatterEditor
            key={open.id}
            matter={open}
            currency={currency}
            who={who}
            onSave={(next) => onSave(matters.map((m) => (m.id === next.id ? next : m)))}
            onDelete={async () => {
              await onSave(matters.filter((m) => m.id !== open.id));
              onOpen(null);
            }}
            onDecide={onDecide}
          />
        )}
        {!starting && !open && matters.length > 0 && <p className={c.muted}>{t("Pick a matter on the left, or bring a new one.")}</p>}
      </div>
    </div>
  );
}

function StartMatter({ onCreate, onCancel }: { onCreate: (template: string, title: string) => void; onCancel?: () => void }) {
  const [title, setTitle] = useState("");
  const [tpl, setTpl] = useState("project");
  return (
    <Card title="Bring a new matter" sub="Name it, pick a starting point, and change anything after">
      <Field label="What do you want to decide?">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("e.g. A second baggage belt, the cleaning contract, a night-shift policy")}
          aria-label={t("Name of the matter")}
          autoFocus
        />
      </Field>
      <div className={c.templates} role="radiogroup" aria-label={t("Starting point")}>
        {MATTER_TEMPLATES.map((x) => (
          <button
            key={x.key}
            type="button"
            role="radio"
            aria-checked={tpl === x.key}
            className={`${c.template} ${tpl === x.key ? c.templateOn : ""}`}
            onClick={() => setTpl(x.key)}
          >
            <b>{t(x.label)}</b>
            <span>{t(x.what)}</span>
          </button>
        ))}
      </div>
      <div className={c.rowEnd}>
        {onCancel && <Button onClick={onCancel}>{t("Cancel")}</Button>}
        <Button variant="primary" onClick={() => onCreate(tpl, title)} data-testid="create-matter">
          {t("Start weighing it")}
        </Button>
      </div>
    </Card>
  );
}

const RISK_LABEL = ["", "low", "medium", "high"] as const;
const num = (v: string): number | null => (v.trim() === "" || !Number.isFinite(Number(v)) ? null : Number(v));

function MatterEditor({
  matter,
  currency,
  who,
  onSave,
  onDelete,
  onDecide,
}: {
  matter: Matter;
  currency: string;
  who: string;
  onSave: (m: Matter) => Promise<"platform" | "browser">;
  onDelete: () => Promise<void>;
  onDecide: MattersProps["onDecide"];
}) {
  const [m, setM] = useState<Matter>(matter);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const e = useMemo(() => evaluateMatter(m), [m]);

  const change = (next: Matter) => {
    setM(next);
    setDirty(true);
    setStatus(null);
  };
  const save = async (next: Matter = m) => {
    const where = await onSave(next);
    setDirty(false);
    setStatus(where === "platform" ? t("Saved on the platform, under your name.") : t("Saved in this browser only — the platform did not take it."));
  };

  const setOption = (key: string, patch: Partial<MatterOption>) =>
    change({ ...m, options: m.options.map((o) => (o.key === key ? { ...o, ...patch } : o)) });
  const setCriterion = (key: string, patch: Partial<Criterion>) =>
    change({ ...m, criteria: m.criteria.map((k) => (k.key === key ? { ...k, ...patch } : k)) });
  const setScore = (opt: string, crit: string, v: number | null) =>
    change({ ...m, scores: { ...m.scores, [opt]: { ...m.scores[opt], [crit]: v == null ? undefined : Math.max(0, Math.min(10, v)) } } });

  const scored = m.criteria.filter((k) => k.source === "score");

  return (
    <article className={c.editor} data-testid="matter-editor" aria-label={m.title}>
      <header className={c.editorHead}>
        <div>
          <div className={c.eyebrow}>{m.category}</div>
          <h2 className={c.editorTitle}>{m.title}</h2>
        </div>
        <div className={c.rowEnd}>
          {dirty && <span className={c.unsaved}>{t("Unsaved changes")}</span>}
          {status && !dirty && <span className={c.savedNote}>{status}</span>}
          <Button variant="primary" onClick={() => void save()} disabled={!dirty} data-testid="save-matter">
            {t("Save")}
          </Button>
        </div>
      </header>

      <ResultPanel m={m} e={e} currency={currency} />

      <Step n={1} title={t("What is the matter?")} hint={t("One line on what has to be decided, and by when.")}>
        <div className={c.formGrid}>
          <Field label="Name">
            <Input value={m.title} onChange={(ev) => change({ ...m, title: ev.target.value })} aria-label={t("Name of the matter")} />
          </Field>
          <Field label="The question, in one line">
            <Input
              value={m.question}
              onChange={(ev) => change({ ...m, question: ev.target.value })}
              placeholder={t("e.g. Which contractor gives us the best service for the money?")}
              aria-label={t("The question")}
            />
          </Field>
          <Field label="Decide by">
            <Input type="date" value={m.decideBy ?? ""} onChange={(ev) => change({ ...m, decideBy: ev.target.value || undefined })} aria-label={t("Decide by")} />
          </Field>
          <Field label="How much it matters">
            <Select value={m.impact} onChange={(ev) => change({ ...m, impact: ev.target.value as BoardRowKey })} aria-label={t("How much it matters")}>
              {BOARD_ROWS.map((r) => (
                <option key={r.key} value={r.key}>
                  {t(r.label)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Step>

      <Step n={2} title={t("Your choices")} hint={t("Name each option. Add the numbers you have — leave blank what you do not know.")}>
        <div className={c.tableWrap}>
          <table className={c.table}>
            <thead>
              <tr>
                <th>{t("choice")}</th>
                <th>{t("cost ({currency})", { currency })}</th>
                <th>{t("benefit ({currency})", { currency })}</th>
                <th>{t("months")}</th>
                <th>{t("risk")}</th>
                <th>{t("note")}</th>
                <th aria-label={t("remove")} />
              </tr>
            </thead>
            <tbody>
              {m.options.map((o) => (
                <tr key={o.key}>
                  <td>
                    <Input value={o.name} onChange={(ev) => setOption(o.key, { name: ev.target.value })} aria-label={t("name of the choice")} />
                  </td>
                  <td>
                    <Input type="number" min={0} value={o.cost ?? ""} onChange={(ev) => setOption(o.key, { cost: num(ev.target.value) })} aria-label={t("cost of {name}", { name: o.name })} />
                  </td>
                  <td>
                    <Input type="number" min={0} value={o.benefit ?? ""} onChange={(ev) => setOption(o.key, { benefit: num(ev.target.value) })} aria-label={t("benefit of {name}", { name: o.name })} />
                  </td>
                  <td>
                    <Input type="number" min={0} value={o.months ?? ""} onChange={(ev) => setOption(o.key, { months: num(ev.target.value) })} aria-label={t("months for {name}", { name: o.name })} />
                  </td>
                  <td>
                    <Select
                      value={o.risk ?? ""}
                      onChange={(ev) => setOption(o.key, { risk: ev.target.value ? (Number(ev.target.value) as 1 | 2 | 3) : null })}
                      aria-label={t("risk of {name}", { name: o.name })}
                    >
                      <option value="">—</option>
                      {[1, 2, 3].map((r) => (
                        <option key={r} value={r}>
                          {t(RISK_LABEL[r])}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <Input value={o.note ?? ""} onChange={(ev) => setOption(o.key, { note: ev.target.value })} aria-label={t("note on {name}", { name: o.name })} />
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={m.options.length <= 1}
                      onClick={() => change({ ...m, options: m.options.filter((x) => x.key !== o.key) })}
                      aria-label={t("remove {name}", { name: o.name })}
                    >
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          size="sm"
          disabled={m.options.length >= 8}
          onClick={() => change({ ...m, options: [...m.options, { key: newId("o"), name: t("Option {n}", { n: m.options.length + 1 }) }] })}
        >
          + {t("Add a choice")}
        </Button>
      </Step>

      <Step n={3} title={t("What matters, and how much")} hint={t("Each thing you care about, weighted 0 (not at all) to 5 (above everything). Scores can come from your numbers or from your judgment.")}>
        <div className={c.criteria}>
          {m.criteria.map((k) => (
            <div key={k.key} className={c.criterion}>
              <Input value={k.name} onChange={(ev) => setCriterion(k.key, { name: ev.target.value })} aria-label={t("name of the criterion")} />
              <label className={c.weight}>
                <span>{t("weight")}</span>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.5}
                  value={k.weight}
                  onChange={(ev) => setCriterion(k.key, { weight: Number(ev.target.value) })}
                  aria-label={t("weight of {name}", { name: k.name })}
                />
                <b>{k.weight}</b>
              </label>
              <Select value={k.source} onChange={(ev) => setCriterion(k.key, { source: ev.target.value as CriterionSource })} aria-label={t("how {name} is scored", { name: k.name })}>
                {(Object.keys(SOURCE_LABEL) as CriterionSource[]).map((src) => (
                  <option key={src} value={src}>
                    {t(SOURCE_LABEL[src])}
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="ghost" onClick={() => change({ ...m, criteria: m.criteria.filter((x) => x.key !== k.key) })} aria-label={t("remove {name}", { name: k.name })}>
                ×
              </Button>
            </div>
          ))}
        </div>
        <Button
          size="sm"
          disabled={m.criteria.length >= 10}
          onClick={() => change({ ...m, criteria: [...m.criteria, { key: newId("c"), name: t("New criterion"), weight: 3, source: "score" }] })}
        >
          + {t("Add a criterion")}
        </Button>
      </Step>

      <Step
        n={4}
        title={t("Score each choice")}
        hint={scored.length ? t("0 is poor, 10 is excellent. Criteria worked out from your numbers are filled in for you.") : t("Every criterion is worked out from your numbers — nothing to score by hand.")}
      >
        {scored.length > 0 && (
          <div className={c.tableWrap}>
            <table className={c.table}>
              <thead>
                <tr>
                  <th>{t("choice")}</th>
                  {scored.map((k) => (
                    <th key={k.key}>{k.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {m.options.map((o) => (
                  <tr key={o.key}>
                    <td className={c.optName}>{o.name}</td>
                    {scored.map((k) => (
                      <td key={k.key}>
                        <Input
                          type="number"
                          min={0}
                          max={10}
                          value={m.scores[o.key]?.[k.key] ?? ""}
                          onChange={(ev) => setScore(o.key, k.key, num(ev.target.value))}
                          aria-label={t("score of {option} on {criterion}", { option: o.name, criterion: k.name })}
                          className={c.scoreInput}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Step>

      <Discussion m={m} e={e} currency={currency} who={who} onChange={(next) => {
          setM(next);
          void save(next);
        }}
      />

      <DecideMatter
        m={m}
        e={e}
        onDecide={async (option, rationale) => {
          const next = { ...m, decided: { option, rationale, at: new Date().toISOString(), by: who } };
          setM(next);
          await save(next);
          await onDecide(next, option, rationale, summaryLines(next, e));
        }}
      />

      <div className={c.rowEnd}>
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            if (window.confirm(t("Remove “{name}” from your matters? Its recorded decision stays in the log.", { name: m.title }))) void onDelete();
          }}
        >
          {t("Remove this matter")}
        </Button>
      </div>
    </article>
  );
}

function Step({ n, title, hint, children }: { n: number; title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className={c.step} aria-label={title}>
      <div className={c.stepHead}>
        <span className={c.stepN}>{n}</span>
        <div>
          <h3 className={c.stepTitle}>{title}</h3>
          <p className={c.stepHint}>{hint}</p>
        </div>
      </div>
      <div className={c.stepBody}>{children}</div>
    </section>
  );
}

/** Three lines for the log, from the arithmetic. */
export function summaryLines(_m: Matter, e: MatterEvaluation): string[] {
  const lines: string[] = [];
  if (e.leader) lines.push(t("{option} scored {score} out of 100 on the criteria as weighted.", { option: e.leader.name, score: e.leader.total }));
  if (e.runnerUp && e.margin != null) lines.push(t("Next was {option} on {score}, {margin} points behind.", { option: e.runnerUp.name, score: e.runnerUp.total, margin: e.margin }));
  if (e.flips[0]) lines.push(flipSentence(e.flips[0]));
  return lines;
}

function ResultPanel({ m, e, currency }: { m: Matter; e: MatterEvaluation; currency: string }) {
  const sorted = [...e.rows].sort((a, b) => a.rank - b.rank);
  const callText =
    e.call === "tie"
      ? t("A tie — the numbers do not separate them; this one is judgment.")
      : e.call === "narrow"
        ? t("A narrow lead — small changes could reverse it.")
        : e.call === "clear"
          ? t("A clear lead.")
          : t("Not enough yet to call it.");
  return (
    <section className={c.result} aria-label={t("The result")} data-testid="matter-result">
      <div className={c.resultHead}>
        <div className={c.eyebrow}>{t("The result so far")}</div>
        {e.leader ? (
          <p className={c.resultLine}>
            <b>{e.leader.name}</b> {t("leads with {score} out of 100.", { score: e.leader.total })}{" "}
            {e.margin != null && tn(e.margin, "{n} point ahead.", "{n} points ahead.")} <span className={c.call}>{callText}</span>
          </p>
        ) : (
          <p className={c.resultLine}>{callText}</p>
        )}
      </div>
      <ul className={c.bars}>
        {sorted.map((r) => (
          <li key={r.key} className={c.barRow}>
            <span className={c.barName}>
              {r.rank}. {r.name}
            </span>
            <span className={c.barTrack}>
              <span className={`${c.barFill} ${r.key === e.leader?.key ? c.barLead : ""}`} style={{ width: `${r.total}%` }} />
            </span>
            <span className={c.barN}>{r.total}</span>
          </li>
        ))}
      </ul>
      <div className={c.resultCols}>
        <div>
          <div className={c.colTitle}>{t("What would change it")}</div>
          {e.flips.length ? (
            <ul className={c.plain}>
              {e.flips.slice(0, 3).map((f) => (
                <li key={f.criterion}>{flipSentence(f)}</li>
              ))}
            </ul>
          ) : (
            <p className={c.muted}>{e.leader ? t("No single weight changes the leader — it holds.") : "—"}</p>
          )}
        </div>
        <div>
          <div className={c.colTitle}>{t("Value for money")}</div>
          {e.value.length ? (
            <ul className={c.plain}>
              {e.value.map((v) => {
                const cost = m.options.find((o) => o.key === v.key)?.cost ?? 0;
                return <li key={v.key}>{t("{option}: {points} points per 100,000 ({cost})", { option: v.name, points: v.perHundredK, cost: money(cost, currency) })}</li>;
              })}
            </ul>
          ) : (
            <p className={c.muted}>{t("Add costs to the choices to see this.")}</p>
          )}
        </div>
        <div>
          <div className={c.colTitle}>{t("Still missing")}</div>
          {e.gaps.length ? (
            <ul className={c.plain}>
              {e.gaps.slice(0, 4).map((g) => (
                <li key={g}>{g}</li>
              ))}
              {e.gaps.length > 4 && <li>{t("and {n} more", { n: e.gaps.length - 4 })}</li>}
            </ul>
          ) : (
            <p className={c.muted}>{t("Nothing — every choice is scored on every criterion.")}</p>
          )}
        </div>
      </div>
    </section>
  );
}

const QUICK = [
  { label: "Which is best?", ask: "which is best?" },
  { label: "What would change it?", ask: "what would change the answer?" },
  { label: "The case against", ask: "what is the case against the leader?" },
  { label: "Value for money", ask: "which is the best value for money?" },
  { label: "What is missing?", ask: "what is missing?" },
];

function Discussion({ m, e, currency, who, onChange }: { m: Matter; e: MatterEvaluation; currency: string; who: string; onChange: (m: Matter) => void }) {
  const cfg = useMemo(() => loadConfig(), []);
  const connected = hasModel(cfg);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = (entries: Matter["discussion"]) => onChange({ ...m, discussion: [...m.discussion, ...entries] });
  const entry = (body: string, from: Matter["discussion"][number]["from"], by: string) => ({ id: newId("d"), at: new Date().toISOString(), by, text: body, from });

  const ask = async (q: string) => {
    const question = q.trim();
    if (!question) return;
    const mine = entry(question, "you", who);
    const own = answerMatter(question, m, e);
    if (own) {
      add([mine, entry(own, "room", t("the room"))]);
      return;
    }
    if (!connected) {
      add([mine]);
      return;
    }
    setBusy(true);
    try {
      const reply = await askModel(cfg, ROOM_MODEL_PROMPT, `Question: ${question}\n\nMaterial:\n${materialForMatter(m, e, currency)}`);
      const bad = verifyFigures(reply.text, e.figures);
      add([
        mine,
        bad.length
          ? entry(t("Withheld: the reply stated figures the room did not produce ({figures}).", { figures: bad.slice(0, 4).join(", ") }), "withheld", t("withheld"))
          : entry(reply.text, "model", reply.model),
      ]);
    } catch (err) {
      add([mine, entry(t("The model could not be reached: {error}", { error: err instanceof Error ? err.message : String(err) }), "withheld", t("error"))]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Talk it through" sub="Your notes and questions, kept with the matter. The room answers from its own arithmetic; a connected model can go wider, and every figure it uses is checked.">
      <div className={c.quick}>
        {QUICK.map((q) => (
          <button key={q.label} type="button" className={c.chip} onClick={() => void ask(q.ask)} disabled={busy}>
            {t(q.label)}
          </button>
        ))}
      </div>
      <form
        className={c.ask}
        onSubmit={(ev) => {
          ev.preventDefault();
          void ask(text);
          setText("");
        }}
      >
        <textarea
          className={c.textarea}
          value={text}
          onChange={(ev) => setText(ev.target.value)}
          placeholder={t("Write a note or ask a question — e.g. what if the budget is cut? who else should we hear from?")}
          aria-label={t("Note or question on this matter")}
          rows={2}
        />
        <Button type="submit" variant="primary" disabled={busy || !text.trim()}>
          {busy ? t("Asking…") : t("Add")}
        </Button>
      </form>
      {!connected && <p className={c.muted}>{t("Questions the room cannot answer from its arithmetic are kept as notes. Connect a model in Settings for wider answers.")}</p>}
      {m.discussion.length > 0 && (
        <ol className={c.thread} data-testid="matter-thread">
          {[...m.discussion].reverse().map((d) => (
            <li key={d.id} className={`${c.post} ${c[`from_${d.from}`] ?? ""}`}>
              <div className={c.postMeta}>
                {d.by} · {d.at.slice(0, 16).replace("T", " ")}
              </div>
              <p className={c.postText}>{d.text}</p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function DecideMatter({ m, e, onDecide }: { m: Matter; e: MatterEvaluation; onDecide: (option: string, rationale: string) => Promise<void> }) {
  const [option, setOption] = useState(m.decided?.option ?? e.leader?.key ?? m.options[0]?.key ?? "");
  const [rationale, setRationale] = useState("");
  return (
    <Card title="Make the decision" sub="Recorded under your name in the decision record, with the result as it stood">
      {m.decided && (
        <p className={c.decidedNote}>
          {t("Decided {date} by {name}: {option} — {why}", {
            date: m.decided.at.slice(0, 10),
            name: m.decided.by,
            option: m.options.find((o) => o.key === m.decided!.option)?.name ?? m.decided.option,
            why: m.decided.rationale,
          })}
        </p>
      )}
      <form
        className={c.decide}
        onSubmit={(ev) => {
          ev.preventDefault();
          if (!rationale.trim() || !option) return;
          void onDecide(option, rationale.trim());
          setRationale("");
        }}
      >
        <Field label="I choose">
          <Select value={option} onChange={(ev) => setOption(ev.target.value)} aria-label={t("choice for {name}", { name: m.title })}>
            {m.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.name}
                {o.key === e.leader?.key ? ` — ${t("leading")}` : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="because">
          <Input
            value={rationale}
            onChange={(ev) => setRationale(ev.target.value)}
            placeholder={t("why this, and what was accepted to get it")}
            aria-label={t("reason for {name}", { name: m.title })}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!rationale.trim()}>
          {m.decided ? t("Record a new decision") : t("Record the decision")}
        </Button>
      </form>
    </Card>
  );
}
